<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link to="/app/questions/list" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Add Question</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Course <span class="text-danger">*</span></label>
                  <select v-model="form.courseId" class="form-select" required>
                    <option value="">Select Course</option>
                    <option v-for="course in courses" :key="course.id" :value="course.id">{{ course.name }}</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Question Type <span class="text-danger">*</span></label>
                  <select v-model="form.type" class="form-select" required @change="resetOptions">
                    <option value="">Select Type</option>
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="true_false">True/False</option>
                    <option value="fill_blank">Fill in the Blank</option>
                  </select>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Question <span class="text-danger">*</span></label>
                <textarea v-model="form.text" class="form-control" rows="3" required></textarea>
              </div>

              <div v-if="form.type === 'multiple_choice'" class="mb-3">
                <label class="form-label">Options</label>
                <div v-for="(option, index) in form.options" :key="index" class="input-group mb-2">
                  <span class="input-group-text">{{ String.fromCharCode(65 + index) }}</span>
                  <input v-model="option.text" type="text" class="form-control" placeholder="Option text" />
                  <div class="input-group-text">
                    <input v-model="option.isCorrect" type="checkbox" class="form-check-input mt-0" @change="handleCorrectChange(index)" />
                    <label class="ms-1 mb-0">Correct</label>
                  </div>
                  <button v-if="form.options.length > 2" type="button" class="btn btn-outline-danger" @click="removeOption(index)">
                    <vue-feather type="x" size="14" />
                  </button>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="addOption">
                  <vue-feather type="plus" size="14" class="me-1" />
                  Add Option
                </button>
              </div>

              <div v-if="form.type === 'true_false'" class="mb-3">
                <label class="form-label">Correct Answer <span class="text-danger">*</span></label>
                <div class="form-check">
                  <input v-model="form.correctAnswer" type="radio" class="form-check-input" value="true" id="true" />
                  <label class="form-check-label" for="true">True</label>
                </div>
                <div class="form-check">
                  <input v-model="form.correctAnswer" type="radio" class="form-check-input" value="false" id="false" />
                  <label class="form-check-label" for="false">False</label>
                </div>
              </div>

              <div v-if="form.type === 'fill_blank'" class="mb-3">
                <label class="form-label">Correct Answer <span class="text-danger">*</span></label>
                <input v-model="form.correctAnswer" type="text" class="form-control" required />
                <small class="text-muted">Use underscore (_____) in the question to indicate the blank</small>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Difficulty</label>
                  <select v-model="form.difficulty" class="form-select">
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Points</label>
                  <input v-model.number="form.points" type="number" class="form-control" min="1" />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Explanation (shown after answer)</label>
                <textarea v-model="form.explanation" class="form-control" rows="2"></textarea>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Create Question
                </button>
                <router-link to="/app/questions/list" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'AddQuestion',
  setup() {
    const router = useRouter()
    const alert = useAlert()
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Questions', url: '/app/questions/list' },
      { title: 'Add Question', active: true }
    ]

    const defaultOptions = () => [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]

    const form = ref({
      courseId: '',
      type: '',
      text: '',
      options: defaultOptions(),
      correctAnswer: '',
      difficulty: 'medium',
      points: 1,
      explanation: ''
    })
    const courses = ref([])
    const loading = ref(false)
    const error = ref('')

    const fetchCourses = async () => {
      try {
        const response = await api.get('/v1/admin/courses')
        courses.value = response.data.courses
      } catch (err) {
        console.error('Failed to fetch courses:', err)
      }
    }

    const resetOptions = () => {
      form.value.options = defaultOptions()
      form.value.correctAnswer = ''
    }

    const addOption = () => form.value.options.push({ text: '', isCorrect: false })
    const removeOption = (index) => form.value.options.splice(index, 1)

    const handleCorrectChange = (index) => {
      form.value.options.forEach((opt, i) => {
        if (i !== index) opt.isCorrect = false
      })
    }

    const handleSubmit = async () => {
      if (form.value.type === 'multiple_choice' && !form.value.options.some(o => o.isCorrect)) {
        error.value = 'Please mark at least one correct answer'
        return
      }

      loading.value = true
      error.value = ''

      try {
        await api.post('/v1/admin/questions', form.value)
        alert.success('Success', 'Question created successfully')
        router.push('/app/questions/list')
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to create question'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchCourses)

    return { breadcrumbs, form, courses, loading, error, resetOptions, addOption, removeOption, handleCorrectChange, handleSubmit }
  }
}
</script>
